import { DateOnlyInput } from "../components/DateOnlyInput";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api";
import { alphaSave } from "../ui/alpha-save";
import { ErrorState, Loading } from "../ui/State";
type Model = {
  case: { id: string; title: string; category: string; status: string };
  documents: { id: string; versionId: string; title: string }[];
  history: {
    id: string;
    detail: {
      kind: string;
      occurredOn: string;
      note: string;
      actionId: string | null;
    };
  }[];
};
export function ResidentCasesAlpha() {
  const [q] = useSearchParams(),
    building = q.get("building"),
    [items, setItems] =
      useState<
        { id: string; title: string; category: string; status: string }[]
      >(),
    [selected, setSelected] = useState(""),
    [data, setData] = useState<Model>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const pending = useRef<{ url: string; body: object } | null>(null);
  const load = async () => {
    setItems(
      (
        await api<{ items: NonNullable<typeof items> }>(
          "/api/alpha/resident-cases?building=" + building,
        )
      ).items,
    );
    if (selected) setData(await api("/api/alpha/resident-cases/" + selected));
  };
  useEffect(() => {
    setData(undefined);
    if (building) void load().catch(setError);
  }, [building, selected]);
  async function write(body: object) {
    setBusy(true);
    setError(undefined);
    pending.current ??= {
      url: "/api/alpha/resident-cases/" + selected + "/record",
      body: { ...body, idempotencyKey: crypto.randomUUID() },
    };
    try {
      await alphaSave(pending, pending.current.url, {
        method: "POST",
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      await load();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    void write({
      category: f.get("category"),
      kind: f.get("kind"),
      occurredOn: f.get("date"),
      note: f.get("note"),
      expectedHistoryCount: data!.history.length,
      evidence: f.getAll("evidence").map((id) => {
        const d = data!.documents.find((d) => d.id === id)!;
        return { documentId: d.id, versionId: d.versionId };
      }),
    });
  }
  return (
    <>
      <h1>Lease breach and nuisance cases</h1>
      <p>
        Choose a reported case from Mail or resident requests. Record
        allegations, responses and agreed remedies separately. Escalation
        prepares an Action for review; it does not send notices or begin
        enforcement.
      </p>
      {Boolean(error) && <ErrorState error={error} />}{" "}
      {pending.current && !busy && (
        <button onClick={() => void write({})}>Retry the same save</button>
      )}
      {!building ? (
        <p>Open casework from a building.</p>
      ) : !items ? (
        <Loading />
      ) : (
        <section className="panel">
          <h2>Reported cases</h2>
          {items.length===0&&<p>No reported cases are recorded for this building. Review incoming correspondence or resident requests in Communications.</p>}
          {items.map((i) => (
            <p key={i.id}>
              <button
                disabled={busy || !!pending.current}
                onClick={() => setSelected(i.id)}
              >
                {i.title}
              </button>{" "}
              · {i.status}
            </p>
          ))}
        </section>
      )}
      {data && (
        <section className="panel form">
          <h2>{data.case.title}</h2>
          <p>{data.case.status}</p>
          <h3>Evidence chronology</h3>
          {data.history.map((h) => (
            <article key={h.id}>
              <h4>
                {h.detail.occurredOn} · {h.detail.kind}
              </h4>
              <p>{h.detail.note}</p>
              {h.detail.actionId && (
                <p>Review Action retained in the Actions register.</p>
              )}
            </article>
          ))}
          <form key={data.history.length} onSubmit={submit}>
            <fieldset
              disabled={
                busy ||
                !!pending.current ||
                ["Resolved", "Closed"].includes(data.case.status)
              }
            >
              <legend>Add to chronology</legend>
              <label>
                Case type
                <select
                  name="category"
                  defaultValue={
                    data.case.category === "Lease breach"
                      ? "Lease breach"
                      : "Nuisance"
                  }
                >
                  <option>Lease breach</option>
                  <option>Nuisance</option>
                </select>
              </label>
              <label>
                Record type
                <select name="kind">
                  {(data.history.length
                    ? [
                        "Report",
                        "Response",
                        "Agreed remedy",
                        "Escalation review",
                        "Completion",
                      ]
                    : ["Report"]
                  ).map((k) => (
                    <option key={k}>{k}</option>
                  ))}
                </select>
              </label>
              <label>
                Occurred on
                <DateOnlyInput name="date" required />
              </label>
              <label>
                Report, response or agreed remedy
                <textarea
                  name="note"
                  required
                  minLength={10}
                  maxLength={10000}
                />
              </label>
              <fieldset>
                <legend>Supporting documents</legend>
                {data.documents.map((d) => (
                  <label key={d.id}>
                    <input type="checkbox" name="evidence" value={d.id} />
                    {d.title}
                  </label>
                ))}
              </fieldset>
              <button>Save case record</button>
            </fieldset>
          </form>
        </section>
      )}
    </>
  );
}
